import i18next from 'i18next';

import {hasValue, exhaustiveCheck} from 'web-app/util/typescript';
import {type ProfileImageProps} from 'web-app/react/components/profile-image/profile-image';
import {type AuthChallengeFragment} from 'web-app/react/login/__generated__/queries.api-types';

import {type AvailableUserContexts, type UserContexts, type CurrentUserContext} from './types';

interface ContextContent {
    id: string;
    type: string;
    title: string;
    images: ProfileImageProps['src'];
}

type ChallengeChoiceTarget = AuthChallengeFragment['choices'][0]['context']['target'];

export const getTitleForTarget = (target: ChallengeChoiceTarget) => {
    switch (target.__typename) {
        case 'InstitutionSet':
            return target.title;
        case 'PersonContextTarget': {
            switch (target.children.length) {
                case 0:
                    return '-';
                case 1:
                    return target.children[0].name.fullName;
                default:
                    return target.children.map(child => child.name.firstName).join(', ');
            }
        }
        default:
            return '-';
    }
};

export const getImageUrlForTarget = (target: ChallengeChoiceTarget): ProfileImageProps['src'] => {
    switch (target.__typename) {
        case 'InstitutionSet':
            return target.profileImage?.url ?? 'img/empty-institution.jpg';
        case 'PersonContextTarget':
            return getImagesForPersonContextTarget(target);
        default:
            return 'img/empty.jpg';
    }
};

const getImagesForPersonContextTarget = (
    personContextTarget: Extract<ChallengeChoiceTarget, {__typename: 'PersonContextTarget'}>,
): ProfileImageProps['src'] => {
    return personContextTarget.children.map(child => {
        return {
            name: child.name.firstName,
            image: child.profileImage?.url,
        };
    });
};

export const getContextContent = (context: AvailableUserContexts[0]): ContextContent | null => {
    const title = getTitleForTarget(context.target);

    switch (context.target.__typename) {
        case 'InstitutionSet':
            return {
                id: context.id,
                type: i18next.t('employee'),
                title,
                images: getImageUrlForTarget(context.target),
            };
        case 'PersonContextTarget':
            return {
                id: context.id,
                type: i18next.t('userContexts.childrensAccount'),
                title,
                images: getImagesForPersonContextTarget(context.target),
            };
        case 'Person':
        case 'NoContextTarget':
            return null;
        default:
            exhaustiveCheck(context.target);
            return null;
    }
};

interface HeaderInformation {
    name: string | null;
    imageUrl?: string | null;
    title?: string;
}

// Account is sadly any due to not being typed all the way down to the definition in the reducer
export const getInformationForHeader = (
    currentUserContext: CurrentUserContext | undefined,
    account: any,
    shouldUseUserContexts: boolean,
): HeaderInformation => {
    if (!hasValue(account)) {
        return {
            name: null,
        };
    }

    const imageUrl = account.image?.small;
    const defaultName = account.name?.fullName;

    const fallback = {
        name: defaultName,
        imageUrl,
        title: account.email,
    };

    if (!hasValue(currentUserContext) || !shouldUseUserContexts) {
        return fallback;
    }

    switch (currentUserContext.target.__typename) {
        case 'InstitutionSet':
            return {
                name: defaultName,
                imageUrl,
                title: currentUserContext.target.title,
            };
        case 'PersonContextTarget':
            return {
                name: currentUserContext.target.person.name.fullName,
                imageUrl,
                title: account.email,
            };
        case 'Person':
            return {
                name: null,
            };
        case 'NoContextTarget':
            return fallback;
        default:
            exhaustiveCheck(currentUserContext.target);
            return {
                name: null,
            };
    }
};

// Used for presenting the user the available options for contexts.
// Only show the choice if there are two or more options.
export const getAvailableContextsToSwitchTo = (
    userContexts: UserContexts | undefined,
): AvailableUserContexts | null => {
    if (!userContexts) {
        return null;
    }

    const {currentContext, availableContexts} = userContexts;

    // We're only interested in the case where we have more than one available context (as the list also includes our current context)
    if (!availableContexts || availableContexts.length <= 1) {
        return null;
    }

    if (!currentContext) {
        return availableContexts;
    }

    // Avoid showing the option to switch to the current context
    return availableContexts.filter(context => context.id !== currentContext.id);
};
